import { InputAdornment, OutlinedInput, type OutlinedInputProps } from "@mui/material";
import { useId, type ReactNode } from "react";

export type FieldState = "default" | "hover" | "focused" | "disabled" | "error" | "filled";

type FieldProps = Omit<
  OutlinedInputProps,
  "size" | "label" | "error" | "startAdornment" | "endAdornment"
> & {
  label?: string;
  hint?: string;
  /** When set, replaces hint text and applies error styling. String renders as helper text; boolean only sets the error visual. */
  error?: string | boolean;
  /**
   * Forces a visual state for design-system showcasing. Under the MUI wrapper,
   * only "disabled" and "error" are fully honored; "hover" / "focused" /
   * "filled" are accepted for API compatibility but rely on MUI's native
   * pseudo-class styling and won't force a non-interactive preview the way
   * the prior hand-rolled Field did.
   */
  state?: FieldState;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Trailing unit label, e.g. "bags", "USD". */
  unit?: string;
};

const WRAPPER_BASE = "flex w-full flex-col gap-1";

/**
 * Brand text field — wraps `@mui/material/OutlinedInput` (which owns the input
 * element, refs, accessibility, adornments, autofill detection) inside our
 * own external `<label>` + helper-text structure. The visual exactly matches
 * the prior Tailwind Field: 4px radius, grey-200 border, shadow-flat, hover
 * grey-300, focus grey-500, error red-100 bg + red-600 border + shadow-flat-red.
 *
 * Consumer API is unchanged: `label`, `hint`, `error` (string | boolean),
 * `state`, `leftIcon`, `rightIcon`, `unit`, plus standard input attrs.
 */
export function Field({
  label,
  hint,
  error,
  state,
  leftIcon,
  rightIcon,
  unit,
  className,
  disabled,
  id,
  sx,
  ...rest
}: FieldProps) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = `${inputId}-hint`;

  const isError = Boolean(error) || state === "error";
  const isDisabled = disabled || state === "disabled";
  const errorText = typeof error === "string" ? error : undefined;
  const helpText = isError ? (errorText ?? hint) : hint;

  return (
    <div className={[WRAPPER_BASE, className ?? ""].filter(Boolean).join(" ")}>
      {label && (
        <label
          htmlFor={inputId}
          className={["text-small font-medium", isError ? "text-red-600" : "text-grey-900"].join(
            " ",
          )}
        >
          {label}
        </label>
      )}

      <OutlinedInput
        id={inputId}
        disabled={isDisabled}
        error={isError}
        aria-describedby={helpText ? helperId : undefined}
        startAdornment={
          leftIcon ? (
            <InputAdornment position="start" sx={{ marginRight: "6px", color: "#a7b0ba" }}>
              {leftIcon}
            </InputAdornment>
          ) : undefined
        }
        endAdornment={
          rightIcon || unit ? (
            <InputAdornment position="end" sx={{ marginLeft: "6px", gap: "6px" }}>
              {unit && (
                <span aria-hidden className="shrink-0 text-body font-medium text-grey-900">
                  {unit}
                </span>
              )}
              {rightIcon && (
                <span className="flex shrink-0 items-center text-grey-500">{rightIcon}</span>
              )}
            </InputAdornment>
          ) : undefined
        }
        sx={{
          borderRadius: "4px",
          backgroundColor: isError ? "#fff2f2" : isDisabled ? "#f6f8fa" : "#ffffff",
          // text-small
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "-0.01em",
          color: isError ? "#f74d4d" : "#4e5966", // red-600 / grey-900
          boxShadow: isDisabled
            ? "none"
            : isError
              ? "0px 1px 3px #f74d4d52" // shadow-flat-red
              : "0px 1px 3px #181d2214", // shadow-flat
          transition: "background-color 120ms, box-shadow 120ms, border-color 120ms",
          "& input": {
            padding: "8px 0", // py-2
            "&::placeholder": {
              color: isError ? "#f74d4d" : "#a7b0ba", // red-600 / grey-500
              opacity: 1,
            },
            "&:disabled": {
              cursor: "not-allowed",
              color: "#a7b0ba",
              WebkitTextFillColor: "#a7b0ba",
            },
          },
          // The outline border lives in a child fieldset. Style it via the notch class.
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isError ? "#f74d4d" : "#e5ebf1", // red-600 / grey-200
            borderWidth: "1px",
            // Padding-x = 12px (px-3) — applied to the input slot below; the outline
            // itself sits on the container's edge.
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: isError ? "#f74d4d" : "#d9e0e8", // red-600 / grey-300
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: isError ? "#f74d4d" : "#a7b0ba", // red-600 / grey-500
            borderWidth: "1px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#f74d4d",
          },
          "&.Mui-disabled": {
            cursor: "not-allowed",
          },
          // Container horizontal padding (px-3 = 12px).
          paddingLeft: "12px",
          paddingRight: "12px",
          ...sx,
        }}
        {...rest}
      />

      {helpText && (
        <p
          id={helperId}
          className={["text-small", isError ? "text-red-600" : "text-grey-500"].join(" ")}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}
